/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  FormEvent,
  MouseEvent,
  SyntheticEvent,
  useCallback,
  useEffect,
  useState
} from 'react';
import DeviceChips from '@ion/applications/src/pages/devices/DeviceChips';
import {translate} from '@ion/core/src/locale/locale';
import {
  DeviceMoveEditorFormFields
} from '@ion/core/src/types/editors';
import {
  Device
} from '@ion/core/src/types/entities';
import {isObjectEmpty} from '@ion/core/src/utils/ObjectUtil';
import {isValue} from '@ion/core/src/utils/StringUtil';
import Chip from '@ion/dls/src/components/chip/Chip';
import FormGroupFields from '@ion/dls/src/components/forms/FormGroupFields';
import {FormItem} from '@ion/dls/src/components/forms/FormItem';
import {FormSection} from '@ion/dls/src/components/forms/FormSection';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {
  useAnimatedMount
} from '../hooks/useAnimatedMount';
import {
  useExtendStyles
} from '../hooks/useExtendStyles';
import {useFormState} from '../hooks/useFormState';
import {DLSTheme, IconSize} from '../types/types';
import {deserialize, serialize} from './DeviceEditorUtils';
import EditorDialog from './EditorDialog';
import styles from './editorStyles';
import GroupChooser from './GroupChooser';

const styleOverrides = (theme: DLSTheme) => ({
  chipContainer : {
    display  : 'flex',
    flexWrap : 'wrap'
  },
  chipWrapper : {
    marginBottom : '10px',
    position     : 'relative'
  },
  targetAccount : {
    marginTop : '-50px'
  }
});

const dialogOverrides = (theme: DLSTheme) => ({
  form : {
    height : '100%'
  },
  formPaper : {
    minWidth    : '70% !important',
    paddingLeft : '2px !important',
    height      : '521px',
    minHeight   : 'unset !important'
  },
  paperScrollPaper : {
    [theme.breakpoints.down('sm')] : {
      minHeight : '100%'
    }
  }
});

// defines the Device bulk editor prop type definitions ...
interface DeviceMoveEditorProps {
  open: boolean;
  devices?: Device[];
  clear?: boolean;
  loading?: boolean;
  onSave: (devices: Device[]) => void;
  onCancel?: (event: SyntheticEvent<{}, Event>) => void;
}

// defines the text input prop type definitions ...
interface TargetType {
  name: DeviceMoveEditorFormFields,
  value: unknown;
  required?: boolean
}

/**
 * The Device Bulk edit Form with set fields and encapsulated logic for handling the form's state
 *
 * @param {boolean} open Determines if the DeviceMoveEditor dialog should be open
 * @param {Function} onCancel Callback function fired when the dialog is to be closed.
 * @param {Function} onSave Function runs when the form is submitted.
 *   The function is passed an device object
 * @param {Device[]} devices Devices that passed to the form
 * @param {boolean} clear If true we clear the form state
 * @param {boolean} loading disables any for submission while the form is busy
 */
export const DeviceMoveEditor = ({
  open,
  onSave,
  onCancel,
  devices = [],
  clear = false,
  loading = false
}: DeviceMoveEditorProps) => {
  const editorStyles                          = useExtendStyles(styles, styleOverrides);
  const classes                               = editorStyles();
  const {isVisible, onRemove}                 = useAnimatedMount();
  const [formState, setFormState]             = useFormState();
  const [groupsLoading, setGroupsLoading]     = useState(false);
  const [deviceDatas, setDeviceDatas]         = useState<Device[]>();
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [isFormChanged, setIsFormChanged]     = useState(false);
  const entityLabel                           = translate('common:entityLabels.devices');

  useEffect(() => {
    if (!isObjectEmpty(devices) && open) {
      const deserializedDevices = devices.map(device => deserialize(device));
      setDeviceDatas(deserializedDevices);
      setIsFormChanged(false);
      setSelectedDevices(devices);
    }
  }, [open, devices]);

  useEffect(() => {
    if (clear) {
      clearForm();
    }
  }, [clear]);

  function clearForm() {
    setFormState({}, true);
    setIsFormChanged(false);
  };

  function handleChooserChange({name, value, required = false}) {
    setFormState({[name]: {name, value: isValue(value, 'none') ? null : value, required}});
    setIsFormChanged(true);
  };

  function handleSubmit(e: FormEvent<HTMLFormElement> | MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    const deviceSerializeDatas = deviceDatas?.map(device => {
      const newDevice = {
        ...device,
        account : {
          key   : 'account',
          value : formState?.account?.value
        }
      };
      return serialize({state: newDevice});
    });
    onSave?.(deviceSerializeDatas);
  };

  const onClose = useCallback(() => {
    onRemove(onCancel);
  }, [onCancel]);

  const onChipDelete = useCallback((selectedItem, selectedDevices) => {
    const filteredDevices = selectedDevices.filter((device) => {
      return device.id !== selectedItem.id;
    });
    const deserializedDevices = filteredDevices.map(device => deserialize(device));
    setDeviceDatas(deserializedDevices);
    setSelectedDevices(filteredDevices);
  }, []);

  const onGroupChange = useCallback((value) => {
    handleChooserChange({
      name : DeviceMoveEditorFormFields.Account,
      value
    });
  }, []);

  const onDelete = useCallback(
    (selected: Device) => () => {
      onChipDelete(selected, selectedDevices);
    }, [selectedDevices]);

  return (
    <EditorDialog open={isVisible}
      dialogTitle={translate('common:editors.move.title', {
        entity : entityLabel
      })}
      onCloseDialog={onClose}
      onCancelDialog={onClose}
      onPrimaryButtonClick={handleSubmit}
      primaryButtonDisabled={!isFormChanged}
      primaryButtonLoading={loading}
      styleOverrides={dialogOverrides} >
      <form className={classes.form} onSubmit={handleSubmit}>
        <div className={classes.formWrapper}>
          <FormSection title={translate('admin-devices:form.move.selected')}>
            <FormGroupFields>
              <FormItem fullWidth>
                <div className={classes.chipContainer}>
                  <DeviceChips devices={selectedDevices} chipWrapperClass={classes.chipWrapper} onDelete={onDelete} />
                </div>
              </FormItem>
            </FormGroupFields>
          </FormSection>
          <div className={classes.targetAccount}>
            <FormSection title={translate('admin-devices:form.move.targetAccount')}>
              <FormGroupFields>
                <FormItem fullWidth>
                  <GroupChooser onLoad={setGroupsLoading}
                    showAccountId={true}
                    includeGroupProperties={false}
                    onChange={onGroupChange}
                    hasError={formState[DeviceMoveEditorFormFields.Account]?.error}
                    defaultValue={formState[DeviceMoveEditorFormFields.Account]?.value} />
                </FormItem>
              </FormGroupFields>
            </FormSection>
          </div>
        </div>
      </form>
    </EditorDialog>
  );
};

export default DeviceMoveEditor;
